import React, { useState } from "react";
import { Smartphone, Copy, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { triggerMobilePanicButton } from "../../../service/securityService";

type ZoneOpt = { value: string; label: string };

const PanicMobileTab: React.FC<{ zones: ZoneOpt[] }> = ({ zones }) => {
  const [location, setLocation] = useState("");
  const [zone, setZone] = useState("");
  const [description, setDescription] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const apiPath = "POST /api/security/mobile/panic-button";

  const copyPath = () => {
    void navigator.clipboard.writeText(
      `${window.location.origin.replace(/\/$/, "")}/api/security/mobile/panic-button`,
    );
    setCopied(true);
    toast.info("Base URL copied (append path when calling from mobile)");
    setTimeout(() => setCopied(false), 2000);
  };

  const sendTestPanic = async () => {
    try {
      setSending(true);
      await triggerMobilePanicButton({
        panicSource: "mobile",
        location: location.trim() || undefined,
        zone: zone || undefined,
        description: description.trim() || undefined,
        coordinates:
          lat.trim() && lng.trim()
            ? { lat: parseFloat(lat), lng: parseFloat(lng) }
            : undefined,
      });
      toast.success("Panic incident created (same pipeline as mobile app)");
      setDescription("");
    } catch (e: any) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.resMessage ||
          "Failed to trigger panic",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-full p-4 md:p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-[#0fa9a6]" />
          Staff panic button (mobile)
        </h3>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">
          The native mobile client should call the authenticated API below with the
          signed-in user. This page lets supervisors test the flow from the desk;
          it creates a <strong>Critical</strong> Security incident and triggers
          integration hooks where configured.
        </p>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
        <code className="text-sm font-mono text-slate-800">{apiPath}</code>
        <button
          type="button"
          onClick={copyPath}
          className="flex items-center gap-1 text-sm px-3 py-1.5 bg-white border rounded-lg hover:bg-slate-100"
        >
          {copied ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
          Copy base hint
        </button>
      </div>

      <div className="text-xs text-gray-600 space-y-1 font-mono bg-gray-50 p-3 rounded border border-gray-100">
        <p>Body JSON (all optional except auth):</p>
        <pre className="whitespace-pre-wrap text-[11px]">
          {`{
  "panicSource": "mobile",
  "location": "string",
  "zone": "accessZoneId",
  "description": "string",
  "coordinates": { "lat": 0, "lng": 0 }
}`}
        </pre>
      </div>

      <div className="max-w-xl space-y-4 border border-red-100 bg-red-50/40 rounded-xl p-5">
        <p className="text-sm font-medium text-red-900">Desk test (creates real incident)</p>
        <div>
          <label className="text-sm text-gray-700">Location</label>
          <input
            className="mt-1 w-full border rounded-md p-2 bg-white"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Where help is needed"
          />
        </div>
        <div>
          <label className="text-sm text-gray-700">Zone</label>
          <select
            className="mt-1 w-full border rounded-md p-2 bg-white"
            value={zone}
            onChange={(e) => setZone(e.target.value)}
          >
            <option value="">—</option>
            {zones.map((z) => (
              <option key={z.value} value={z.value}>
                {z.label}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-sm text-gray-700">Lat</label>
            <input
              className="mt-1 w-full border rounded-md p-2 bg-white"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm text-gray-700">Lng</label>
            <input
              className="mt-1 w-full border rounded-md p-2 bg-white"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-700">Note</label>
          <input
            className="mt-1 w-full border rounded-md p-2 bg-white"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <button
          type="button"
          disabled={sending}
          onClick={sendTestPanic}
          className="w-full py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50"
        >
          {sending ? "Sending…" : "Trigger test panic (mobile)"}
        </button>
      </div>
    </div>
  );
};

export default PanicMobileTab;
